import React from 'react'

import { useTranslatedText } from './StaticTextContext'
import { ModerationSeverity } from './moderationUtils'

interface OracleModerationMessageProps {
  /** The severity level of the moderation violation */
  severity: ModerationSeverity
  /** Whether to show crisis resources (988 hotline, etc.) */
  showCrisisResources: boolean
  /** Optional additional CSS class */
  className?: string
}

/**
 * Displays moderation error messages inline in chat.
 * Used by both OraclePage (Sage on the Stage) and OracleSideGuide (Guide on the Side).
 *
 * Features:
 * - Severity-based styling (border colors, backgrounds)
 * - Crisis resources with country disclaimer for self-harm content
 * - Logging notice for threatening content
 * - Generic fallback for unknown moderation types
 */
export const OracleModerationMessage: React.FC<OracleModerationMessageProps> = ({
  severity,
  showCrisisResources,
  className = '',
}) => {
  // Get translated text for the severity level
  const title = useTranslatedText({ keys: ['moderation', severity, 'title'] })
  const body = useTranslatedText({ keys: ['moderation', severity, 'body'] })

  // Crisis resources text
  const crisisTitle = useTranslatedText({ keys: ['moderation', 'crisisTitle'] })
  const crisisBody = useTranslatedText({ keys: ['moderation', 'crisisBody'] })
  const crisisInternational = useTranslatedText({ keys: ['moderation', 'crisisInternational'] })
  const crisisPhoneLabel = useTranslatedText({ keys: ['moderation', 'crisisPhoneLabel'] })

  // Logging notice for threatening content
  const loggingNotice = useTranslatedText({ keys: ['moderation', 'loggingNotice'] })

  const severityClass = severity.toLowerCase().replace(/_/g, '-')
  const showLoggingNotice = severity === 'CRITICAL_THREAT'

  return (
    <div className={`oracle-moderation-message ${className}`.trim()}>
      <div className={`oracle-moderation-message__container oracle-moderation-message--${severityClass}`}>
        <div className="oracle-moderation-message__icon">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M12 9V13M12 17H12.01M12 3L2 21H22L12 3Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div className="oracle-moderation-message__content">
          <h4 className="oracle-moderation-message__title">{title}</h4>
          <p className="oracle-moderation-message__body">{body}</p>

          {showCrisisResources && (
            <div className="oracle-moderation-message__crisis">
              <p className="oracle-moderation-message__crisis-title">{crisisTitle}</p>
              <p className="oracle-moderation-message__crisis-body">{crisisBody}</p>
              <a
                href="tel:988"
                className="oracle-moderation-message__crisis-phone"
                aria-label={crisisPhoneLabel}
              >
                988
              </a>
              <p className="oracle-moderation-message__crisis-international">
                {crisisInternational}
                {' '}
                <a
                  href="https://findahelpline.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="oracle-moderation-message__crisis-link"
                >
                  findahelpline.com
                </a>
              </p>
            </div>
          )}

          {showLoggingNotice && (
            <p className="oracle-moderation-message__notice">{loggingNotice}</p>
          )}
        </div>
      </div>
    </div>
  )
}

export default OracleModerationMessage
